import { useEffect, useState } from 'react'
import type { EventRecord } from '@romain130492/topchopsticks'
import { api } from '../lib/api'
import { useI18n } from '../hooks/useI18n'
import { useEventBooking } from '../hooks/useEventBooking'
import { setWeChatShareCard, shareEvent, eventShareUrl } from '../lib/share'
import { resetPageMeta, setPageMeta, shareImageUrl } from '../lib/meta'
import EventCard from '../components/EventCard'
import LoadingState from '../components/LoadingState'

/**
 * One event, on its own page — the target of a shared link and of the
 * triple-tap shortcut, mirroring pages/eventDetail in the mini program.
 *
 * It renders the SAME EventCard with `forceOpen`, so the detail body is
 * identical to the one behind "See details" on the feed. Nothing can be
 * visible in one place and missing in the other.
 */
export default function EventDetailPage({
  eventId, registered, onRegister, onBack
}: {
  eventId: string
  registered: boolean
  onRegister: (reason?: string) => void
  onBack: () => void
}) {
  const { t, L } = useI18n()
  const [event, setEvent] = useState<EventRecord | null>(null)
  const [missing, setMissing] = useState(false)

  const load = () => api.getEvent(eventId).then(setEvent).catch(() => setMissing(true))
  const booking = useEventBooking({ onChanged: load })

  useEffect(() => { load() }, [eventId])

  /**
   * Everything a share needs, per event.
   *
   * Same image precedence as the mini program's buildEventShare():
   * cover -> first picture -> the app default. Never an empty string, because
   * an empty imgUrl gives WeChat nothing and the card comes out blank.
   */
  useEffect(() => {
    if (!event) return
    const title = L(event.title as never)
    const desc = L(event.description as never)
    const image = event.cover_image_url || event.pictures?.[0]?.sizes?.original || shareImageUrl()
    const link = eventShareUrl(event)

    // Title + og tags + the offscreen <img> WeChat actually looks for.
    setPageMeta({ title, description: desc, image, url: link })
    // And the real card, when the Official Account is configured.
    setWeChatShareCard({ title, desc, link, imgUrl: image })

    return () => resetPageMeta()
  }, [event, L])

  if (missing) {
    return (
      <div className="py-16 text-center">
        <p className="text-13 text-muted">{t('ev.notFound')}</p>
        <button type="button" onClick={onBack} className="mt-4 text-13 text-gold underline">
          {t('common.back')}
        </button>
      </div>
    )
  }
  if (!event) return <LoadingState />

  return (
    <div>
      <button type="button" onClick={onBack} className="mb-2 py-1 text-h2 leading-none text-gold">←</button>
      <EventCard
        event={event}
        forceOpen
        registered={registered}
        onRegister={onRegister}
        booked={Boolean(booking.seatFor(event.id))}
        busy={booking.busyEventId === event.id}
        seatsReady={booking.ready}
        onReserve={booking.reserve}
        onCancel={booking.cancel}
        attendeesKey={booking.version}
        onShare={(e) => shareEvent(e, {
          title: L(e.title as never),
          desc: L(e.description as never),
          copied: t('ev.linkCopied'),
          hint: t('ev.shareHint')
        })}
      />
    </div>
  )
}
